import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { BackLink, Card, Chip, Icon } from '@/components/ui';
import { useAsyncData } from '@/lib/hooks/useAsyncData';
import { useT } from '@/lib/i18n';

import { fetchMenu, type DishSummaryDto, type MenuDto } from './api';
import { DishPane } from './DishPane';

export function MenuScreen({
  shopSlug,
  ownerContext = false,
}: {
  shopSlug: string;
  ownerContext?: boolean;
}) {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();

  const lang = searchParams.get('lang') ?? '';

  const catFromUrl = searchParams.get('cat') ?? '';

  const { data: menu, error, loading } = useAsyncData<MenuDto>(
    `${shopSlug}|${lang}`,
    (signal) => fetchMenu(shopSlug, lang, signal),
    t('menu.loadFailed'),
  );

  const railRef = useRef<HTMLDivElement>(null);

  const activeCategoryId = useMemo(() => {
    if (!menu || menu.categories.length === 0) return '';
    const fromUrl = menu.categories.find((c) => c.id === catFromUrl);
    return (fromUrl ?? menu.categories[0])?.id ?? '';
  }, [menu, catFromUrl]);

  const dishesInCategory = useMemo(() => {
    if (!menu || !activeCategoryId) return [];

    return menu.dishes.filter((d) => d.category_id === activeCategoryId);
  }, [menu, activeCategoryId]);

  const [chosenDishId, setChosenDishId] = useState<string>('');

  const selectedDish = useMemo(() => {
    if (dishesInCategory.length === 0) return null;

    const chosen = dishesInCategory.find((d) => d.id === chosenDishId);
    if (chosen) return chosen;

    const serverDefault = dishesInCategory.find((d) => d.id === menu?.default_dish_id);
    return serverDefault ?? dishesInCategory[0] ?? null;
  }, [dishesInCategory, chosenDishId, menu?.default_dish_id]);

  if (loading) return <MenuSkeleton />;

  if (error || !menu) {
    return (
      <main className="mx-auto max-w-[420px] px-6 py-16">
        <Card pad>
          <div className="flex items-start gap-3">
            <span className="text-amber-600">
              <Icon name="warn" size={20} />
            </span>
            <div>
              <h1 className="text-h3 text-plum-900">{t('menu.unavailable')}</h1>
              <p className="text-body-s mt-1 text-ink-600">{error}</p>
            </div>
          </div>
        </Card>
      </main>
    );
  }

  const selectedIndex = dishesInCategory.findIndex((d) => d.id === selectedDish?.id);
  const firstIndex = selectedIndex <= 0;
  const lastIndex = selectedIndex < 0 || selectedIndex >= dishesInCategory.length - 1;

  function scrollRail(delta: number) {
    const next = dishesInCategory[selectedIndex + delta];
    if (!next) return;
    setChosenDishId(next.id);

    const index = selectedIndex + delta;
    const node = railRef.current?.children[index] as HTMLElement | undefined;
    node?.scrollIntoView({ block: 'nearest' });
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-[460px] bg-paper pb-6 lg:max-w-none">
      
      <header className="flex items-center gap-[10px] border-b border-line px-4 py-3 lg:px-8">
        <BackLink to="/shop/shop_A" label={t('menu.backToLanguages')} size="sm" />
        <div className="min-w-0 flex-1">
          
          <h1 className="truncate text-[16px] leading-tight font-bold tracking-[-.01em] text-plum-900">
            {menu.shop.name}
          </h1>
          {menu.shop.name_ja ? (
            <p className="truncate text-[11.5px] leading-tight text-ink-500">
              {menu.shop.name_ja}
            </p>
          ) : null}
        </div>
        <Link
          to={`/shop/${menu.shop.slug}?lang=${menu.language}`}
          className="inline-flex shrink-0 items-center gap-[6px] rounded-full border border-transparent bg-plum-100 px-[11px] py-[6px] text-[12px] font-bold text-plum-800 no-underline"
          aria-label={t('menu.changeLanguage')}
        >
          <Icon name="globe" size={15} />
          {menu.language.toUpperCase()}
        </Link>
      </header>

      
      {menu.language_fallback_applied ? (
        <div className="border-b border-amber-100 bg-amber-100/60 px-4 py-2">
          <p className="text-caption flex items-center gap-1.5 text-amber-600">
            <Icon name="info" size={13} />
            {t('menu.languageFallback', { language: menu.language.toUpperCase() })}
          </p>
        </div>
      ) : null}

      
      <nav
        className="flex gap-2 overflow-x-auto border-b border-line px-4 py-3"
        aria-label={t('menu.categories')}
      >
        {menu.categories.map((cat) => (
          <Chip
            key={cat.id}
            selected={cat.id === activeCategoryId}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.set('cat', cat.id);
              setSearchParams(next, { replace: true });
            }}
          >
            {cat.name}
            {cat.fallback_applied ? (
              <span className="text-[10px] opacity-70" title={t('common.notTranslated')}>
                *
              </span>
            ) : null}
          </Chip>
        ))}
      </nav>

      
      
      <p className="px-4 pt-3 pb-0 text-[12px] text-ink-500 lg:px-8">
        {t('menu.slideToSeeMore')}
      </p>

      
      
      <div className="grid grid-cols-[116px_1fr] items-start gap-[10px] px-3 pt-3 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-6 lg:px-8 lg:pt-6 xl:grid-cols-[260px_minmax(0,780px)] xl:justify-center">

        
        <div className="flex min-w-0 flex-col gap-[6px]">
          <RailNav
            direction="prev"
            disabled={firstIndex}
            onClick={() => scrollRail(-1)}
          />

          <div
            ref={railRef}
            className="flex flex-col gap-[6px] overflow-y-auto"
            role="listbox"
            aria-label={t('menu.dishes')}

            style={{ maxHeight: 5 * 62 + 4 * 6 }}
          >
            {dishesInCategory.map((dish) => (
              <DishRailItem
                key={dish.id}
                dish={dish}
                selected={dish.id === selectedDish?.id}
                onSelect={() => setChosenDishId(dish.id)}
              />
            ))}
          </div>

          <RailNav
            direction="next"
            disabled={lastIndex}
            onClick={() => scrollRail(1)}
          />

          
          {ownerContext ? (
            <Link
              to={`/shop/${shopSlug}/manage/menu`}
              className="flex flex-col items-center gap-0.5 rounded-[9px] border-[1.5px] border-dashed border-[var(--color-green-400)] bg-paper py-2.5 text-[10px] font-semibold text-green-700 no-underline"
            >
              <Icon name="check" size={15} />
              {t('ownerMenu.addItem')}
            </Link>
          ) : null}

          
          {dishesInCategory.length > 5 ? (
            <p className="text-center text-[10px] text-ink-500">
              {t('ownerMenu.dishesCount', { count: dishesInCategory.length })}
            </p>
          ) : null}
        </div>

        
        <div className="min-w-0 flex-1">
          {selectedDish ? (
            <DishPane
              shopSlug={menu.shop.slug}
              dish={selectedDish}
              language={menu.language}
            />
          ) : (
            <Card pad>
              <p className="text-body-s text-ink-500">{t('menu.noDishesHere')}</p>
            </Card>
          )}
        </div>
      </div>
    </main>
  );
}

function DishRailItem({
  dish,
  selected,
  onSelect,
}: {
  dish: DishSummaryDto;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={[
        'grid place-items-center rounded-[9px] px-[10px] py-[12px] text-center',
        'text-[11.5px] leading-[1.35] transition-[background-color,color,transform]',
        'active:scale-[.97]',
        selected
          ? 'bg-[var(--color-green-700)] font-bold text-white'
          : 'bg-ink-50 font-medium text-ink-700 hover:bg-ink-100',
      ].join(' ')}
      style={selected ? { boxShadow: 'var(--shadow-sm)' } : undefined}
    >
      {dish.name}
      {dish.fallback_applied ? (
        <span className="ml-1 text-[10px] opacity-70" aria-hidden="true">
          *
        </span>
      ) : null}
    </button>
  );
}

function RailNav({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'prev' ? t('menu.previousDish') : t('menu.nextDish')}
      className={[
        'grid h-[26px] place-items-center rounded-[6px] text-plum-700',
        'transition-colors hover:bg-plum-50 disabled:opacity-30 disabled:hover:bg-transparent',
      ].join(' ')}
    >
      <Icon name={direction === 'prev' ? 'chevron-up' : 'chevron-down'} size={18} />
    </button>
  );
}

function MenuSkeleton() {
  const t = useT();
  return (
    <main className="mx-auto max-w-[460px] lg:max-w-none" aria-busy="true" aria-live="polite">
      <span className="sr-only">{t('menu.loading')}</span>
      <div className="flex gap-2 border-b border-line px-4 py-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-9 w-20 animate-pulse rounded-full bg-ink-100" />
        ))}
      </div>
      <div className="flex gap-3 px-4 pt-4">
        <div className="w-[124px] shrink-0 space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-[var(--radius-md)] bg-ink-100" />
          ))}
        </div>
        <div className="flex-1 space-y-3">
          <div className="h-40 animate-pulse rounded-[var(--radius-md)] bg-ink-100" />
          <div className="h-6 w-40 animate-pulse rounded bg-ink-100" />
        </div>
      </div>
    </main>
  );
}
